import React from "react";

import Mousetrap from "mousetrap";
import { Button } from "@showitinc/showit-wc";
import EventUtils from "../../utils/EventUtils";
import GalleryAssetListItem from "./GalleryAssetListItem";
import GalleryAssetListContainer from "./GalleryAssetListContainer";
import AppConstants from "../../constants/AppConstants";
import characterCountInputMax from "../characterCountInputMax";
import Glyphicon from "../base/Glyphicon";
import { GALLERY_ASSET } from "../../constants/DnDConstants";

import styles from "./GalleryAssetList.module.css";

class GalleryAssetList extends React.Component {
  static defaultProps = {
    assets: [],
    expanded: false,
  };

  componentDidMount() {
    this._mousetrap = new Mousetrap();
    this._mousetrap.bind(["del", "backspace"], this._handleDeleteKey);
  }

  componentWillUnmount() {
    this._mousetrap.reset();
    this._mousetrap = null;
  }

  _handleDeleteKey = (e) => {
    this._removeFromList();
    return false;
  };

  _onImageMove = (data) => {
    if (this.props.onReorderList) {
      this.props.onReorderList(data);
    }
  };

  _onImageSelect = (asset, index, e) => {
    const selectionType = EventUtils.selectionType(e);

    const selection = {
      asset,
      index,
      rangeSelect: selectionType.range,
      multiSelect: selectionType.multi,
      type: GALLERY_ASSET,
      event: e.type,
    };

    if (this.props.onItemSelect) {
      this.props.onItemSelect(selection);
    }
  };

  _addToList = () => {
    if (this.props.onAddToList) {
      this.props.onAddToList();
    }
  };

  _removeFromList = () => {
    if (this.props.onRemoveFromList) {
      this.props.onRemoveFromList();
    }
  };

  _getAssetList = (assets) => {
    const { expanded, galleryEditEnabled } = this.props;

    return (
      <GalleryAssetListContainer expanded={expanded} galleryEditEnabled={galleryEditEnabled}>
        {assets.map(function (asset, idx) {
          return (
            <GalleryAssetListItem
              key={`asset-${asset.key}-${idx}`}
              asset={asset}
              index={idx}
              onMoveImage={this._onImageMove}
              onMouseDown={this._onImageSelect.bind(this, asset, idx)}
              onMouseUp={this._onImageSelect.bind(this, asset, idx)}
              gridMode={expanded}
            />
          );
        }, this)}
      </GalleryAssetListContainer>
    );
  };

  _expandGallery = () => {
    if (this.props.onExpand) {
      this.props.onExpand();
    }
  };

  _contractGallery = () => {
    if (this.props.onContract) {
      this.props.onContract();
    }
  };

  _getOrganizeView = () => {
    const { assets, numSelected } = this.props;

    const assetList = this._getAssetList(assets);
    const removeBtn = this._getRemoveButton();

    return (
      <div style={{ display: "flex", height: "100%", flex: "1 1 auto" }}>
        <div className={styles["collapse-btn"]} onClick={this._contractGallery}>
          <span className="icon icon-arrow-right" />
        </div>
        <div style={{ display: "flex", height: "100%", flex: "1 1 auto", flexDirection: "column" }}>
          <div
            className="si-dialog-header"
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <div>Gallery Images</div>
          </div>
          {assetList}
          {removeBtn}
        </div>
      </div>
    );
  };

  _getSelectionView = () => {
    const { assets, addToListEnabled, addToListLabel, numSelected, numAssetsSelected } =
      this.props;
    const hasSelected = numSelected > 0;
    const hasAssets = assets.length > 0;

    let assetList = null;

    if (hasAssets) {
      assetList = this._getAssetList(assets);
    } else {
      assetList = (
        <GalleryAssetListContainer
          expanded={false}
          galleryEditEnabled={this.props.galleryEditEnabled}
        >
          <div>
            <ol style={{ paddingLeft: 20, paddingRight: 4, fontSize: 14 }}>
              <li style={{ marginBottom: 6 }}>Drag or select images from library.</li>
              <li style={{ marginBottom: 6 }}>
                Tip: You can hold shift + click images to select multiple images at once.
              </li>
              <li style={{ marginBottom: 6 }}>
                Tip: You can hold shift + click images to select multiple images at once.
              </li>
              <li style={{ marginBottom: 6 }}>
                Click{" "}
                <div
                  style={{ fontSize: "12px" }}
                  className="glyphicon glyphicon-circle_arrow_right pulseBlue"
                />{" "}
                to add them to your gallery.
              </li>
              <li style={{ lineHeight: 1.5 }}>
                Click &quot;Save&quot; when you are finished adding images.
              </li>
              <li style={{ lineHeight: 1.5 }}>
                Click &quot;Save&quot; when you are finished adding images.
              </li>
            </ol>
          </div>
        </GalleryAssetListContainer>
      );
    }

    const removeBtn = this._getRemoveButton();
    const galleryCount = characterCountInputMax({
      maxLength: AppConstants.MAX_GALLERY_IMAGES,
      baseClass: "si-dialog-header",
    });
    return (
      <div className={styles["side-panel"]}>
        <div className={styles["action-area"]}>
          {addToListEnabled ? (
            <div
              className={styles["add-to-gallery-btn"]}
              onClick={this._addToList}
              title="Click To Add"
              data-testid="gallery-add-to-list"
            >
              <div className={styles["gallery-number"]}>{addToListLabel ?? numAssetsSelected}</div>
              <div
                style={{ fontSize: "36px" }}
                className="pulseBlue glyphicon glyphicon-circle_arrow_right"
              />
            </div>
          ) : null}
        </div>
        {hasAssets ? (
          <div className={styles["expand-btn"]} onClick={this._expandGallery}>
            <span className="icon icon-arrow-left" />
          </div>
        ) : null}
        <div className={styles["list-area"]}>
          <div
            className="si-dialog-header"
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <div>Gallery Images</div>
            <div>{galleryCount(assets.length)}</div>
          </div>
          {assetList}
          {removeBtn}
        </div>
      </div>
    );
  };

  _getRemoveButton() {
    const { numSelected } = this.props;

    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "8px" }}>
        {numSelected > 0 ? (
          <Button variant="destructive" size="lg" onClick={this._removeFromList}>
            <Glyphicon glyph="bin" />
          </Button>
        ) : null}
      </div>
    );
  }

  render() {
    return this.props.expanded ? this._getOrganizeView() : this._getSelectionView();
  }
}

export default GalleryAssetList;
